import React from 'react';

import { formatMoney } from '@betterment/core';
import { Box, Button, Dialog, H1, Icon, Table, Text, VStack } from '@betterment/samba-react';

import { formatNumberWithDecimals } from '../../../utils/NumbersHelper';

import type { SecurityGroupPosition } from '../data';

export type AvailableTableColumns = Array<'Asset' | 'Weight' | 'Balance'>;

interface BaseCellProps {
  isLastColumn?: boolean;
}

export interface TableCellsProps {
  columns: AvailableTableColumns;
  position: SecurityGroupPosition;
  showDrawer?: boolean;
  driftThreshold?: number;
}

const RogueTickerModal: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <Dialog.Content size="large" css={{ padding: 'space.400' }}>
        <Dialog.Title asChild={true}>
          <H1 variant="title300" css={{ mb: 'space.150' }}>
            Non-target holdings in a managed portfolio
          </H1>
        </Dialog.Title>
        <Dialog.Description>
          <Text as="div">
            Non-target holdings are positions not included in your client’s target portfolio. If rebalancing is enabled,
            these holdings may be sold as the system works to realign the account with the target portfolio. This can
            result in unintended trades and unwanted gains.
          </Text>
          <Text as="div" css={{ mt: 'space.200', mb: 'space.400' }}>
            Proceeds from any sales will be reinvested according to the target allocation and portfolio drift. If you’d
            like to retain these positions, consider turning off rebalancing or include them as part of your client’s
            portfolio strategy.
          </Text>
        </Dialog.Description>
        <Dialog.Close asChild={true}>
          <Button variant="solidSecondary">Done</Button>
        </Dialog.Close>
      </Dialog.Content>
    </Dialog>
  );
};

export const BaseCell: React.FC<React.PropsWithChildren<BaseCellProps>> = ({ isLastColumn, children }) => {
  return (
    <Table.Cell
      css={{
        '&:last-child': { pr: 'space.0' },
        '&:first-child': { pl: 'space.0' },
      }}
    >
      <VStack css={{ alignItems: isLastColumn ? 'flex-end' : '' }}>{children}</VStack>
    </Table.Cell>
  );
};

export const BalanceCell: React.FC<TableCellsProps> = ({ columns, position, showDrawer }) => {
  const isWeightColumnVariant = columns?.includes('Weight');
  return (
    <Box
      css={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 'space.100',
      }}
    >
      <Box
        css={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
        }}
      >
        <Text variant="text100">{formatMoney(position.amount)}</Text>

        {!isWeightColumnVariant && (
          <Text variant="text50" color="text.weak">
            {formatNumberWithDecimals(position.currentWeight, 1)}%
          </Text>
        )}
      </Box>
      {showDrawer && <Icon name="CaretRight" size="small" />}
    </Box>
  );
};

export const AssetCell: React.FC<Omit<TableCellsProps, 'columns'>> = ({ position }) => {
  return (
    <>
      <Text variant="text100">{position.securityGroup.name}</Text>
      <Text variant="text50" color="text.weak">
        {[...new Set(position.securityPositions.map(sp => sp.security.symbol))].join(', ')}
      </Text>
    </>
  );
};

export const WeightCell: React.FC<Omit<TableCellsProps, 'columns'>> = ({ position, driftThreshold }) => {
  const [isRogueModalOpen, setIsRogueModalOpen] = React.useState(false);
  const isHighDrift = driftThreshold !== undefined && Math.abs(position.drift) > driftThreshold;
  return (
    <Box css={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', textAlign: 'right' }}>
      <RogueTickerModal isOpen={isRogueModalOpen} onClose={() => setIsRogueModalOpen(false)} />
      <Box css={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 'space.50' }}>
        {position.isRogueSecurity && <Icon color="icon.warning" name="WarningFilled" size="small" />}
        <Text as="span" variant="text100">
          {formatNumberWithDecimals(position.currentWeight, 1)}%
        </Text>
      </Box>
      {isHighDrift && !position.isRogueSecurity && (
        <Text as="span" color="text.warning" variant="text50">
          <svg xmlns="http://www.w3.org/2000/svg" width="10" height="8" viewBox="0 0 10 8" fill="none">
            <path d="M10 7.5L0 7.5L5 0.5L10 7.5Z" fill="#B18B21" />
          </svg>{' '}
          High drift ({formatNumberWithDecimals(position.drift, 1)}%)
        </Text>
      )}
      {position.isRogueSecurity && (
        <>
          <Text variant="text50" color="text.warning">
            Not in target portfolio
          </Text>
          <Button variant="textPrimary" onClick={() => setIsRogueModalOpen(true)} css={{ fontSize: 'fontSize.50' }}>
            Learn more
          </Button>
        </>
      )}
    </Box>
  );
};

const getComponent = (
  column: string,
  position: SecurityGroupPosition,
  columns: AvailableTableColumns,
  showDrawer?: boolean,
  driftThreshold?: number
) => {
  switch (column) {
    case 'Balance':
      return <BalanceCell position={position} columns={columns} key={column} showDrawer={showDrawer} />;
    case 'Asset':
      return <AssetCell position={position} key={column} />;
    case 'Weight':
      return <WeightCell position={position} key={column} driftThreshold={driftThreshold} />;
    default:
      return null;
  }
};

export const TableCells: React.FC<TableCellsProps> = ({ position, columns, showDrawer, driftThreshold }) => {
  return (
    <>
      {columns.map((col, index) => {
        const isLastColumn = index === columns.length - 1;
        return (
          <BaseCell isLastColumn={isLastColumn} key={`${col}-${position.id}`}>
            {getComponent(col, position, columns, showDrawer, driftThreshold)}
          </BaseCell>
        );
      })}
    </>
  );
};
